import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorState } from "@/ui/ErrorState";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

function detectChunkError(error: unknown): boolean {
  if (!error) return false;
  const msg = (error instanceof Error ? `${error.name} ${error.message}` : String(error)).toLowerCase();
  return (
    msg.includes("chunkloaderror") ||
    msg.includes("loading chunk") ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("error loading dynamically imported module")
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isChunkError: detectChunkError(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHardReload = async () => {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) {
      console.warn("[ErrorBoundary] cache cleanup failed", e);
    }
    const url = new URL(window.location.href);
    url.searchParams.set("_t", Date.now().toString());
    window.location.replace(url.toString());
  };

  override render() {
    if (this.state.hasError) {
      const { error, isChunkError } = this.state;
      const technical = error?.message ? `${error.name}: ${error.message}` : undefined;

      if (isChunkError) {
        return (
          <ErrorState
            variant="server-error"
            fullPage
            title="Nova versão disponível"
            description="O sistema foi atualizado. Limpe o cache do navegador e recarregue para usar a versão mais recente."
            primaryAction={{ label: "Limpar cache e recarregar", onClick: this.handleHardReload }}
            secondaryAction={{ label: "Recarregar página", onClick: this.handleReload }}
            technicalDetails={technical}
          />
        );
      }

      return (
        <ErrorState
          variant="server-error"
          fullPage
          primaryAction={{ label: "Recarregar página", onClick: this.handleReload }}
          secondaryAction={{ label: "Limpar cache e recarregar", onClick: this.handleHardReload }}
          technicalDetails={technical}
        />
      );
    }

    return this.props.children;
  }
}
